import { useState } from "react";
import { cn } from "../../lib/utils";
import { LoadingRegion, Skeleton } from "../../components/ui/Skeleton";

// ── Formatowanie ──────────────────────────────────────────────────────────────

export const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pl-PL");
};

export const fmtDays = (days: number | null | undefined) =>
  days == null ? "—" : `${Math.round(days)} ${Math.round(days) === 1 ? "dzień" : "dni"}`;

// Część e-maila przed @ (pełny adres w podpowiedzi)
export const shortEmail = (email: string) => email.split("@")[0];

export const OVERDUE_DAYS = 30;

// ── Elementy ──────────────────────────────────────────────────────────────────

type Tone = "neutral" | "amber" | "red" | "green";

const TONE: Record<Tone, { fg: string; bg: string }> = {
  neutral: { fg: "var(--sc-text)", bg: "var(--sc-bg)" },
  amber: { fg: "#C07810", bg: "var(--sc-amber-bg)" },
  red: { fg: "var(--sc-red)", bg: "var(--sc-red-bg)" },
  green: { fg: "var(--sc-green)", bg: "var(--sc-green-bg)" },
};

// Wiek zaległości: do tygodnia zwykły, do 30 dni bursztynowy, dalej czerwony
export const ageTone = (days: number | null | undefined): Tone =>
  days == null ? "neutral" : days > OVERDUE_DAYS ? "red" : days > 7 ? "amber" : "neutral";

export const Pill = ({ tone = "neutral", children, title }: { tone?: Tone; children: React.ReactNode; title?: string }) => (
  <span
    title={title}
    className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap"
    style={{ color: TONE[tone].fg, background: TONE[tone].bg }}
  >
    {children}
  </span>
);

export const DaysPill = ({ days }: { days: number | null | undefined }) => (
  <Pill tone={ageTone(days)}>{fmtDays(days)}</Pill>
);

export const StatCard = ({
  label, value, hint, tone = "neutral",
}: { label: string; value: number | string; hint?: string; tone?: Tone }) => (
  <div className="bg-[var(--sc-surface)] border border-[var(--sc-border)] rounded-xl p-5 flex flex-col gap-1">
    <span className="text-[12px] text-[var(--sc-muted)]">{label}</span>
    <span
      className="text-[32px] font-light tracking-tight leading-none mt-1"
      style={{ color: tone === "neutral" || value === 0 ? "var(--sc-text)" : TONE[tone].fg }}
    >
      {value}
    </span>
    {hint && <span className="text-[12px] text-[var(--sc-muted)] mt-1">{hint}</span>}
  </div>
);

export const Section = ({
  title, description, actions, children, className,
}: {
  title: string;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={cn("bg-[var(--sc-surface)] border border-[var(--sc-border)] rounded-xl p-6", className)}>
    <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
      <div>
        <h2 className="text-[14px] font-semibold text-[var(--sc-text)]">{title}</h2>
        {description && <p className="text-[12px] text-[var(--sc-muted)] mt-1 max-w-3xl">{description}</p>}
      </div>
      {actions}
    </div>
    {children}
  </div>
);

export const Message = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-center justify-center text-[13px] text-[var(--sc-muted)] py-10">{children}</div>
);

export const Segmented = <T extends string | number>({
  options, value, onChange,
}: { options: { label: string; value: T }[]; value: T; onChange: (v: T) => void }) => (
  <div className="flex gap-1 bg-[var(--sc-bg)] border border-[var(--sc-border)] rounded-lg p-1">
    {options.map(o => (
      <button
        key={String(o.value)}
        onClick={() => onChange(o.value)}
        className={cn(
          "text-[12px] px-3 py-1 rounded-md transition-colors whitespace-nowrap",
          value === o.value
            ? "bg-[var(--sc-amber)] text-[#0F0F0F] font-medium"
            : "text-[var(--sc-muted)] hover:text-[var(--sc-text)]"
        )}
      >
        {o.label}
      </button>
    ))}
  </div>
);

export const TH = ({ children, right }: { children?: React.ReactNode; right?: boolean }) => (
  <th className={cn(
    "py-2 pr-4 text-[11px] font-medium text-[var(--sc-muted)] whitespace-nowrap",
    right ? "text-right" : "text-left"
  )}>
    {children}
  </th>
);

export const TD = ({ children, right, className, title }: {
  children?: React.ReactNode; right?: boolean; className?: string; title?: string;
}) => (
  <td title={title} className={cn("py-2.5 pr-4 text-[var(--sc-text)] align-top", right && "text-right tabular-nums", className)}>
    {children}
  </td>
);

// Liczba w tabeli: zero wyszarzone, reszta w kolorze tonu
export const Count = ({ n, tone = "neutral" }: { n: number; tone?: Tone }) =>
  n === 0
    ? <span className="text-[var(--sc-muted)]">0</span>
    : <span className="font-medium" style={{ color: TONE[tone].fg }}>{n}</span>;

export const Table = ({ head, children }: { head: React.ReactNode; children: React.ReactNode }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-[12px]">
      <thead><tr className="border-b border-[var(--sc-border)]">{head}</tr></thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

export const ROW = "border-b border-[var(--sc-border)] last:border-0";

// Pierwsze `limit` pozycji z przyciskiem „Pokaż wszystkie”
export function useLimited<T>(items: T[], limit = 10) {
  const [all, setAll] = useState(false);
  const visible = all ? items : items.slice(0, limit);
  const toggle = items.length > limit ? (
    <button
      onClick={() => setAll(a => !a)}
      className="mt-3 text-[12px] text-[var(--sc-blue)] hover:underline"
    >
      {all ? "Pokaż mniej" : `Pokaż wszystkie (${items.length})`}
    </button>
  ) : null;
  return { visible, toggle };
}

export const LINK = "text-[var(--sc-blue)] hover:underline font-medium";

// ── Wczytywanie ───────────────────────────────────────────────────────────────

// Tabela: nagłówek i wiersze z szarymi paskami (pierwsza kolumna szersza)
export const TableSkeleton = ({ rows = 5, cols = 6 }: { rows?: number; cols?: number }) => (
  <div className="flex flex-col">
    {Array.from({ length: rows }, (_, r) => (
      <div key={r} className="flex items-center gap-4 py-3 border-b border-[var(--sc-border)] last:border-b-0">
        <Skeleton className="h-3 w-[28%]" />
        {Array.from({ length: cols - 1 }, (_, c) => (
          <Skeleton key={c} className="h-3 flex-1 max-w-[80px] ml-auto" />
        ))}
      </div>
    ))}
  </div>
);

// Zakładka statystyk: kafelki (prawdziwe etykiety) i sekcje (prawdziwe tytuły), dane jako skeleton
export const TabSkeleton = ({ cards, sections }: { cards: string[]; sections: string[] }) => (
  <LoadingRegion label="Wczytywanie statystyk" className="space-y-6">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {cards.map((label) => (
        <div key={label} className="bg-[var(--sc-surface)] border border-[var(--sc-border)] rounded-xl p-5 flex flex-col gap-1">
          <span className="text-[12px] text-[var(--sc-muted)]">{label}</span>
          <Skeleton className="h-8 w-14 mt-1" />
          <Skeleton className="h-3 w-28 mt-1.5" />
        </div>
      ))}
    </div>
    {sections.map((title) => (
      <Section key={title} title={title}>
        <TableSkeleton />
      </Section>
    ))}
  </LoadingRegion>
);
